import {
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useParams } from "react-router-dom";

import { WorkspaceRecord } from "@/shared/domain/workspaces/WorkspaceRecord";

import { SearchParams } from "../environment";
import { nanoSecToSecStr } from "../time";

import CopyToClipboardButton from "./CopyToClipboardButton";
import { useWorkspaceTimer } from "./visualization/WorkspaceCtx";

interface SharedWorkspaceLinkModal {
  open: boolean;
  record: WorkspaceRecord | null;
  handleClose: () => void;
  error: Error | null;
}

export const SharedWorkspaceLinkModal: React.FC<SharedWorkspaceLinkModal> = ({
  open,
  record,
  handleClose,
  error,
}) => {
  const theme = useTheme();
  const link = useSharedWorkspaceLink(record);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={"md"} onClose={handleClose}>
      <DialogTitle>Share Workspace</DialogTitle>
      <DialogContent>
        {error !== null ? (
          <DialogContentText id="alert-dialog-description">
            Failed to create shared workspace. Try again.
          </DialogContentText>
        ) : (
          <DialogContentText id="alert-dialog-description" component={"span"}>
            Copy the link below to share your current workspace:
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                fontSize: "0.95rem",
                gap: theme.spacing(1),
              }}
            >
              <Link
                href={link}
                sx={{
                  textDecoration: "none",
                  ":hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {link}
              </Link>
              <CopyToClipboardButton
                text={link}
                eventName={"CopyWorkspaceLinkClicked"}
                eventProps={{ link }}
              />
            </Box>
          </DialogContentText>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Builds a URL for sharing the given workspace.
 */
function useSharedWorkspaceLink(workspaceRecord: WorkspaceRecord | null) {
  const timer = useWorkspaceTimer();
  const { workspaceId } = useParams();

  /**
   * Using window.location over react-router-dom's useLocation to
   * support dev stack branches. useLocation does not include the
   * basename when running on dev stacks.
   */
  const { location } = window;

  const newWorkspaceId = workspaceRecord?.workspace_id || "";
  const url = new URL(location.origin);

  // If there is a workspaceId in the URL, replace it.
  if (workspaceId) {
    url.pathname = location.pathname.replace(workspaceId, newWorkspaceId);
  } else {
    // ...otherwise, append the new workspaceId.
    url.pathname = `${location.pathname}/${newWorkspaceId}`;
  }

  // Clear all search params
  url.search = "";

  // and only set timestamp
  url.searchParams.set(
    SearchParams.TIMESTAMP,
    nanoSecToSecStr(timer.currentTime),
  );

  return url.href;
}
